/**
 * The feed's Mixpanel event catalogue.
 *
 * One function per event RSI's dashboards consume, so the property shapes can
 * be audited in a single place. Names and keys are transcribed verbatim from
 * the legacy Streakers feed widget this feed replaced. See `./mixpanel` for
 * which project each brand reports into and why only production builds do.
 */

import type { FeedCreator, FeedItem } from '../types/feedContract'
import { feedOffering, trackFeedEvent } from './mixpanel'

/**
 * Carousel and compact cards report under a ` Widget`-suffixed event name so
 * RSI can separate embedded placements from the full feed.
 */
export type TrackedCardVariant = 'full' | 'compact'

function variantName(name: string, variant: TrackedCardVariant): string {
  return variant === 'compact' ? `${name} Widget` : name
}

/**
 * What a creator is called in RSI's dashboards. The single place that decides,
 * so the item events and `Follow Button Clicked` can never drift apart —
 * `display_name` is the only value this repo carries that corresponds to the
 * legacy `username`, and if RSI later asks for a different one it changes here.
 */
export function creatorLabel(creator?: Partial<FeedCreator> | null): string {
  return creator?.display_name || creator?.name || ''
}

/**
 * Legacy `actor?.username || '_Recommended Bet'`. Machine-generated inventory
 * had no actor, so it reported under the literal sentinel — this repo states
 * the same thing explicitly via `content_origin`.
 */
function feedItemAuthor(item: FeedItem): string {
  if (item.content_origin && item.content_origin !== 'creator') return '_Recommended Bet'
  return creatorLabel(item.creator) || '_Recommended Bet'
}

/** The media the card actually plays: the first video, else whatever leads. */
function feedItemMedia(item: FeedItem) {
  return item.media?.find((entry) => entry.type === 'video') || item.media?.[0]
}

/** Legacy `object?.mediaURL`. */
function feedItemMediaURL(item: FeedItem): string | undefined {
  const media = feedItemMedia(item)
  return media?.playback?.hls_url || media?.url
}

/** Counts the enabled button click, even when the provider rejects the action. */
export function trackCopyBetButtonClicked(
  item: FeedItem,
  position: number,
  variant: TrackedCardVariant,
  hasSuspendedLegs: boolean,
): void {
  trackFeedEvent(variantName('Copy Bet Button Clicked', variant), {
    'Feed Item ID': item.id,
    'Feed Item Author': feedItemAuthor(item),
    'Card Position': position + 1,
    'Has Suspended Legs': hasSuspendedLegs,
  })
}

/**
 * The viewer's first play or pause of a card's video, once per card.
 *
 * Divergence worth knowing: the legacy event put the *media object's* id in
 * `Feed Item ID` while every neighbouring event put the post id there, so the
 * key meant two different things depending on the event. This sends the post
 * id, which makes `Feed Item ID` mean one thing across the whole catalogue, and
 * carries the media id alongside it so a dashboard that keyed on the old value
 * has a column to move to rather than a hole.
 */
export function trackFeedItemViewed(item: FeedItem, variant: TrackedCardVariant): void {
  trackFeedEvent(variantName('Feed Item Viewed', variant), {
    'Feed Item URL': feedItemMediaURL(item),
    'Feed Item ID': item.id,
    'Media ID': feedItemMedia(item)?.id,
    'Feed Item User': feedItemAuthor(item),
  })
}

/**
 * The event name reports the state the viewer is entering, so pass the mute
 * state as it is *after* the toggle.
 */
export function trackVideoMuteToggled(item: FeedItem, mutedAfterToggle: boolean): void {
  trackFeedEvent(`Video ${mutedAfterToggle ? 'Muted' : 'Unmuted'}`, {
    'Feed Item URL': feedItemMediaURL(item),
    'Feed Item ID': item.id,
    'Feed Item User': feedItemAuthor(item),
  })
}

/**
 * Fires only once the link reaches the clipboard, so a failed copy is never
 * counted as a share.
 */
export function trackShareBet(item: FeedItem, shareURL: string): void {
  trackFeedEvent('Share Bet', {
    'Feed Item ID': item.id,
    Platform: 'Clipboard',
    'Share URL': shareURL,
    Offering: feedOffering(),
  })
}

/**
 * Both directions, distinguished by `Is Unfollow`. Takes the creator rather
 * than a pre-formatted name so the call sites cannot each pick their own.
 */
export function trackFollowButtonClicked(properties: {
  creatorId: string
  creator?: Partial<FeedCreator> | null
  postId?: string
  isUnfollow: boolean
}): void {
  trackFeedEvent('Follow Button Clicked', {
    'Followee ID': properties.creatorId,
    'Followee Username': creatorLabel(properties.creator),
    'Feed Item ID': properties.postId || '',
    'Is Unfollow': properties.isUnfollow,
  })
}

/**
 * Pagination depth. The legacy widget reported only pages after the first, so
 * page 1 stays out of the funnel here too.
 *
 * `Feed Kind` named the legacy route's feed (following / for-you). This repo
 * mounts one surface per embed, so the surface slug carries that meaning.
 */
export function trackFeedNextPage(surface: string, page: number): void {
  if (page <= 1) return
  trackFeedEvent('Feed Next Page', {
    'Feed Kind': surface,
    'Feed Page': page,
  })
}
